import React, { useEffect, useMemo, useRef, useState } from 'react';
import rawMedSchools from '../data/med-schools.json';
import { normalizeMedSchoolData } from '../utils/normalizeMedSchoolData';
import {
  A_LEVEL_OPTIONS,
  filterMedSchools,
  formatResultCount,
  isSchoolListAlphabetical,
  parseCommittedUcatScore,
  sortSchoolsByName,
} from '../utils/filterMedSchools';
import MedSchoolPanel from './MedSchoolPanel';
import './MedSchoolsPage.css';

const normalizedDataset = normalizeMedSchoolData(rawMedSchools);

export default function MedSchoolsPage() {
  const [ucatInput, setUcatInput] = useState('');
  const [committedUcatScore, setCommittedUcatScore] = useState(null);
  const [ucatError, setUcatError] = useState(null);
  const [aLevelMaximum, setALevelMaximum] = useState('all');
  const [openSchoolId, setOpenSchoolId] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const headerRefs = useRef({});

  const schools = useMemo(() => {
    const list = normalizedDataset.schools;
    return isSchoolListAlphabetical(list) ? list : sortSchoolsByName(list);
  }, []);

  const filtered = useMemo(
    () => filterMedSchools(schools, { ucatScore: committedUcatScore, aLevelMaximum }),
    [schools, committedUcatScore, aLevelMaximum]
  );

  useEffect(() => {
    if (openSchoolId && !filtered.some((school) => school.id === openSchoolId)) {
      setOpenSchoolId(null);
    }
  }, [filtered, openSchoolId]);

  const onUcatBlur = () => {
    const result = parseCommittedUcatScore(ucatInput);
    setUcatError(result.error);
    setCommittedUcatScore(result.score);
  };

  const toggleSchool = (id) => {
    const next = openSchoolId === id ? null : id;
    setOpenSchoolId(next);
    if (next && openSchoolId && openSchoolId !== next) {
      requestAnimationFrame(() => {
        const node = headerRefs.current[next];
        if (node && typeof node.scrollIntoView === 'function') {
          const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          node.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
        }
      });
    }
  };

  const entryCycle = normalizedDataset.dataset.entryCycle;
  const ucatFieldId = 'med-schools-ucat';
  const aLevelFieldId = 'med-schools-alevel';
  const helpId = 'med-schools-difficulty-help';

  return (
    <section className="ucat-ws-page med-schools-page">
      <div className="ucat-ws-page-header med-schools-header">
        <div className="med-schools-title-row">
          <div className="med-schools-title-block">
            <h1 className="ucat-ws-page-title">Med Schools</h1>
            <p className="med-schools-subtitle">
              Compare requirements, admissions processes and student life across UK medical schools. Filters narrow the list; always check the complete requirements before applying.
            </p>
          </div>
          {entryCycle ? <p className="med-schools-cycle">{entryCycle}</p> : null}
        </div>
      </div>

      <div className="med-schools-filters">
        <div className="med-schools-field">
          <label htmlFor={ucatFieldId}>UCAT score</label>
          <div className="med-schools-ucat-shell">
            <input
              id={ucatFieldId}
              type="number"
              min={900}
              max={2700}
              step={10}
              inputMode="numeric"
              placeholder="e.g. 2100"
              value={ucatInput}
              onChange={(e) => setUcatInput(e.target.value)}
              onBlur={onUcatBlur}
              aria-invalid={Boolean(ucatError)}
              aria-describedby={ucatError ? 'med-schools-ucat-error' : undefined}
            />
            <span className="med-schools-ucat-suffix">/ 2700</span>
          </div>
          {ucatError ? <p id="med-schools-ucat-error" className="med-schools-error">{ucatError}</p> : null}
        </div>

        <div className="med-schools-field">
          <label htmlFor={aLevelFieldId}>A-level requirement</label>
          <select
            id={aLevelFieldId}
            value={aLevelMaximum}
            onChange={(e) => setALevelMaximum(e.target.value)}
          >
            {A_LEVEL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        <p className="med-schools-filter-note">
          The UCAT filter uses a simple published threshold where one exists. Composite, unpublished and non-UCAT selection methods remain visible for manual checking.
        </p>
      </div>

      <div className="med-schools-meta">
        <p className="med-schools-count" aria-live="polite">{formatResultCount(filtered.length)}</p>
        <div className="med-schools-help">
          <button
            type="button"
            className="med-schools-help-btn"
            aria-expanded={helpOpen}
            aria-controls={helpId}
            onClick={() => setHelpOpen((open) => !open)}
            onBlur={() => setHelpOpen(false)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
              <path d="M12 10.5V17" stroke="currentColor" strokeWidth="1.6" />
              <circle cx="12" cy="7.5" r="1" fill="currentColor" />
            </svg>
            More stars = greater admissions difficulty
          </button>
          {helpOpen ? (
            <p id={helpId} className="med-schools-help-text" role="tooltip">
              A relative admissions measure based on the supplied methodology. It is not a ranking of teaching quality, prestige or student experience.
            </p>
          ) : (
            <p id={helpId} className="med-school-sr-only">
              A relative admissions measure based on the supplied methodology. It is not a ranking of teaching quality, prestige or student experience.
            </p>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="med-schools-empty">
          No medical schools match both filters. Try a higher UCAT score or a broader A-level requirement.
        </div>
      ) : (
        <div className="med-schools-list">
          {filtered.map((school) => (
            <MedSchoolPanel
              key={school.id}
              school={school}
              open={openSchoolId === school.id}
              onToggle={() => toggleSchool(school.id)}
              headerRef={(node) => {
                headerRefs.current[school.id] = node;
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}
